/**
 * Calendar System Configuration
 * 
 * Central configuration file for React Big Calendar with customization for
 * delivery calendar management.
 */

import type { CalendarConfig, ColorSchemes } from '../../types/calendar';

// Color Schemes for Event Categories
export const EVENT_COLORS: ColorSchemes = {
  // By Category
  delivery: {
    bg: '#3b82f6',      // blue-500
    text: '#ffffff',
    border: '#2563eb',  // blue-600
  },
  production: {
    bg: '#10b981',      // green-500
    text: '#ffffff',
    border: '#059669',  // green-600
  },
  pending: {
    bg: '#f59e0b',      // amber-500
    text: '#ffffff',
    border: '#d97706',  // amber-600
  },
  completed: {
    bg: '#6b7280',      // gray-500
    text: '#ffffff',
    border: '#4b5563',  // gray-600
  },
  urgent: {
    bg: '#ef4444',      // red-500
    text: '#ffffff',
    border: '#dc2626',  // red-600
  },
  
  // By State
  locked: {
    bg: '#8b5cf6',      // purple-500
    text: '#ffffff',
    border: '#6d28d9',  // purple-700
  },
  
  // Default
  default: {
    bg: '#3b82f6',      // blue-500
    text: '#ffffff',
    border: '#2563eb',  // blue-600
  },
};

// Main Calendar Configuration
export const CALENDAR_CONFIG: CalendarConfig = {
  // View Settings
  defaultView: 'month',
  views: ['month', 'week', 'day', 'agenda'],
  /** Month view: render all same-day events so row-height logic can fully expand week rows. */
  showAllEventsInMonth: true,

  // Time Settings
  step: 5,                                           // 5-minute intervals
  timeslots: 1,                                      // 1 slot per 5 minutes (full height for each slot)
  scrollToTime: new Date(1970, 0, 1, 7, 0, 0),     // Scroll to 7 AM (our start time)
  min: new Date(1970, 0, 1, 6, 0, 0),               // Start at 6 AM
  max: new Date(1970, 0, 1, 22, 0, 0),              // End at 10 PM
  
  // Responsive / density hints (not wired to react-big-calendar directly)
  eventDensity: {
    threshold: 999,
    compactMode: false,
    maxVisible: 999,
    showMoreText: '',
  },
  
  // Date Formatting (using date-fns patterns)
  formats: {
    dayFormat: 'dd',                        // "01", "15", "31"
    weekdayFormat: 'EEE',                   // "Mon", "Tue", "Wed"
    monthHeaderFormat: 'MMMM yyyy',         // "November 2024"
    dayHeaderFormat: 'EEEE, MMMM dd',       // "Monday, November 20"
    agendaDateFormat: 'EEE MMM dd',         // "Mon Nov 20"
    agendaTimeFormat: 'hh:mm a',            // "09:00 AM"
    timeGutterFormat: 'hh:mm a',            // "09:00 AM"
  },
  
  // Feature Flags
  enableDragAndDrop: false,  // Phase 3+
  enableLockUnlock: true,    // Phase 1
  enableResize: false,       // Phase 3+
  
  // Color Schemes
  colorSchemes: EVENT_COLORS,
};

// Helper function to get event color
export const getEventColor = (colorScheme: string) => {
  return EVENT_COLORS[colorScheme] || EVENT_COLORS.default;
};

// Helper function to get category color
export const getCategoryColor = (categorySlug: string) => {
  return EVENT_COLORS[categorySlug] || EVENT_COLORS.default;
};

// View Labels
export const VIEW_LABELS: Record<string, string> = {
  month: 'Month',
  week: 'Week',
  day: 'Day',
  agenda: 'Agenda',
};

// Calendar Messages
export const CALENDAR_MESSAGES = {
  today: 'Today',
  previous: 'Back',
  next: 'Next',
  month: 'Month',
  week: 'Week',
  day: 'Day',
  agenda: 'Agenda',
  showMore: (count: number) => `+${count} more`,
  noEventsInRange: 'No events in this time range',
  allDay: 'All Day',
  locked: 'Locked',
  loading: 'Loading events...',
  error: 'Failed to load events',
};

// Responsive Configuration
export const getResponsiveConfig = () => {
  if (typeof window === 'undefined') {
    return CALENDAR_CONFIG;
  }

  const width = window.innerWidth;

  // Mobile (< 768px)
  if (width < 768) {
    return {
      ...CALENDAR_CONFIG,
      defaultView: 'agenda' as const,
      views: ['agenda', 'day'] as const,
      eventDensity: {
        ...CALENDAR_CONFIG.eventDensity,
        threshold: 10,
        maxVisible: 3,
      },
    };
  }

  // Tablet (768px - 1024px)
  if (width < 1024) {
    return {
      ...CALENDAR_CONFIG,
      views: ['month', 'week', 'agenda'] as const,
      eventDensity: {
        ...CALENDAR_CONFIG.eventDensity,
        threshold: 20,
        maxVisible: 4,
      },
    };
  }

  // Desktop (>= 1024px)
  return CALENDAR_CONFIG;
};

